import React, {useEffect, useMemo, useState} from 'react';
import {
  MAX_ADDITIONAL_DIGITAL_FILES,
  MAX_DIGITAL_FULFILLMENT_FILES,
} from '../../../../../shared/digitalFulfillment.js';
import MediaLibraryManager from '../Media/MediaLibraryManager';
import {isBrowserPreviewableImage, mediaKind} from '../Media/MediaCard';

function idsMatch(left, right) {
  return left != null && right != null && String(left) === String(right);
}

function formatSize(size) {
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function fileLabel(file) {
  return file?.title || file?.filename || `File ${file?.id || ''}`;
}

function previewUrl(file) {
  if (!file?.cdnUrl) return '';
  return `${file.cdnUrl}?w=160&h=120&etag=${file.etag || file.uuid || ''}`;
}

export default function DigitalFileSetEditor({
  primaryFileId,
  fileIds = [],
  files = [],
  missingFileCount = 0,
  onChange,
}) {
  const [knownFiles, setKnownFiles] = useState(files);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [flash, setFlash] = useState(null);

  useEffect(() => {
    if (!files.length) return;
    setKnownFiles(current => {
      const byId = new Map(current.map(file => [String(file.id), file]));
      for (const file of files) byId.set(String(file.id), file);
      return [...byId.values()];
    });
  }, [files]);

  const selectedFiles = useMemo(() => fileIds.map(id => (
    knownFiles.find(file => idsMatch(file.id, id)) || {id, filename: `File ${id}`}
  )), [fileIds, knownFiles]);

  const atLimit = fileIds.length + (primaryFileId ? 1 : 0)
    >= MAX_DIGITAL_FULFILLMENT_FILES;
  const canAdd = Boolean(primaryFileId) && !atLimit;

  useEffect(() => {
    if (!canAdd) setPickerOpen(false);
  }, [canAdd]);

  const addFiles = newFiles => {
    if (!primaryFileId) return;
    const next = [...fileIds];
    const added = [];
    let skippedAtLimit = false;
    for (const file of newFiles) {
      if (idsMatch(file.id, primaryFileId)) continue;
      if (next.some(id => idsMatch(id, file.id))) continue;
      if (next.length + (primaryFileId ? 1 : 0) >= MAX_DIGITAL_FULFILLMENT_FILES) {
        skippedAtLimit = true;
        break;
      }
      next.push(file.id);
      added.push(file);
    }
    setKnownFiles(current => {
      const byId = new Map(current.map(file => [String(file.id), file]));
      for (const file of newFiles) byId.set(String(file.id), file);
      return [...byId.values()];
    });
    if (added.length) {
      setFlash({
        tone: 'success',
        message: added.length === 1
          ? `“${fileLabel(added[0])}” was added to the delivery files.`
          : `${added.length} files were added to the delivery files.`,
      });
      onChange(next);
    } else if (skippedAtLimit) {
      setFlash({tone: 'error', message: 'The delivery file limit has been reached.'});
    }
  };

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= fileIds.length) return;
    const next = [...fileIds];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const remove = id => onChange(fileIds.filter(fileId => !idsMatch(fileId, id)));

  const removeFromPicker = file => {
    setFlash({
      tone: 'success',
      message: `“${fileLabel(file)}” was removed from the delivery files.`,
    });
    remove(file.id);
  };

  return (
    <div className="digital-file-set-editor">
      <h3>Additional delivery files</h3>

      {selectedFiles.length > 0 ? (
        <>
          <p className="help">
            Customers receive these {selectedFiles.length} additional
            {' '}{selectedFiles.length === 1 ? 'file' : 'files'} after purchase.
          </p>
          <ol className="digital-file-set-list">
            {selectedFiles.map((file, index) => (
              <li key={file.id} className="digital-file-set-row">
                <div className="digital-file-set-preview">
                  {mediaKind(file) === 'image' && isBrowserPreviewableImage(file) && previewUrl(file)
                    ? <img src={previewUrl(file)} alt={fileLabel(file)} />
                    : <i className="far fa-file" aria-hidden="true"></i>}
                </div>
                <div className="digital-file-set-details">
                  <strong>{fileLabel(file)}</strong>
                  <span>
                    {[file.extension?.toUpperCase(), formatSize(file.size)]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
                <div className="digital-file-set-actions">
                  <button
                    type="button"
                    className="button secondary"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    aria-label={`Move ${fileLabel(file)} up`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="button secondary"
                    onClick={() => move(index, 1)}
                    disabled={index === selectedFiles.length - 1}
                    aria-label={`Move ${fileLabel(file)} down`}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="button danger"
                    onClick={() => remove(file.id)}
                  >
                    Remove
                  </button>
                </div>
              </li>
            ))}
          </ol>
        </>
      ) : (
        <p className="help">
          Need to deliver more than one file? Add original files directly instead of making a ZIP
          archive.
        </p>
      )}

      {!primaryFileId && (
        <p className="help">Choose the primary file above before adding other files.</p>
      )}
      {missingFileCount > 0 && (
        <p className="error">
          {missingFileCount} attached {missingFileCount === 1 ? 'file is' : 'files are'} missing.
          Remove or replace the unavailable {missingFileCount === 1 ? 'entry' : 'entries'}.
        </p>
      )}
      {atLimit && (
        <p className="help">
          {MAX_ADDITIONAL_DIGITAL_FILES} additional files maximum (
          {MAX_DIGITAL_FULFILLMENT_FILES} including the primary file).
        </p>
      )}

      {canAdd && (
        <div className="digital-file-set-add-actions">
          <button
            type="button"
            className="button secondary small"
            onClick={() => setPickerOpen(true)}
          >
            Add more delivery files
          </button>
        </div>
      )}

      {pickerOpen && canAdd && (
        <MediaLibraryManager
          variant="modal"
          isOpen
          selectable
          multiSelect
          selectedIds={fileIds}
          flash={flash}
          title="Add delivery files"
          onSelect={file => addFiles([file])}
          onDeselect={removeFromPicker}
          onUpload={file => addFiles([file])}
          onCancel={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
